import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/layout/Layout';
import SEO from '../components/seo/SEO';
import HeroVideo from '../components/home/HeroVideo';
import HeroCarousel from '../components/home/HeroCarousel';
import FeaturedCollections from '../components/home/FeaturedCollections';
import ProductCarousel from '../components/products/ProductCarousel';
import PromoBanner from '../components/home/PromoBanner';
import ProductGridSkeleton from '../components/products/ProductGridSkeleton';
import WeeklyFeatured from '../components/home/WeeklyFeatured';
import SaleGrid from '../components/home/SaleGrid';
import CategoryCarousel from '../components/home/CategoryCarousel';
import SectionDivider from '../components/home/SectionDivider';
import {
  getNewArrivals,
  getBestSellers,
  getSaleProducts,
  getFeaturedProducts,
} from '../services/products';
import { getHeroVideo } from '../services/banners';
import { useToast } from '../hooks/use-toast';
import { BRAND_NAME } from '../lib/brand';
import BrandWordmark from '../components/layout/BrandWordmark';

const HERO_VIDEO_CACHE_KEY = 'gappaq_hero_video';

const readCachedHeroVideo = () => {
  try {
    const raw = sessionStorage.getItem(HERO_VIDEO_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.videoUrl) return null;
    return {
      ...parsed,
      title: parsed.title ?? '',
      subtitle: parsed.subtitle ?? '',
    };
  } catch {
    return null;
  }
};

const HeroVideoPlaceholder = () => (
  <div
    className="relative min-h-[500px] h-[70vh] sm:h-[75vh] lg:h-[80vh] lg:min-h-[600px] xl:min-h-[700px] bg-black"
    aria-hidden="true"
  />
);

const HomePage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [heroVideo, setHeroVideo] = useState(() => readCachedHeroVideo());
  const [heroVideoChecked, setHeroVideoChecked] = useState(false);
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestSellers, setBestSellers] = useState([]);
  const [saleItems, setSaleItems] = useState([]);
  const [featuredItems, setFeaturedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      getHeroVideo()
        .then((config) => {
          if (config?.videoUrl) {
            sessionStorage.setItem(HERO_VIDEO_CACHE_KEY, JSON.stringify(config));
            setHeroVideo(config);
          } else {
            sessionStorage.removeItem(HERO_VIDEO_CACHE_KEY);
            setHeroVideo(null);
          }
          setHeroVideoChecked(true);
        })
        .catch(() => {
          setHeroVideoChecked(true);
        });
    };
    load();
    const onFocus = () => load();
    const onHeroVideoSaved = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('hero-video-saved', onHeroVideoSaved);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('hero-video-saved', onHeroVideoSaved);
    };
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const [newProducts, bestSellerProducts, saleProducts, featuredProducts] = await Promise.all([
          getNewArrivals(),
          getBestSellers(),
          getSaleProducts(),
          getFeaturedProducts(),
        ]);
        setNewArrivals(newProducts);
        setBestSellers(bestSellerProducts);
        setSaleItems(saleProducts);
        setFeaturedItems(featuredProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        toast({
          title: 'Hata',
          description: 'Ürünler yüklenirken bir sorun oluştu. Lütfen sayfayı yenileyin.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [toast]);

  return (
    <Layout>
      <SEO
        title={`${BRAND_NAME} | Ana Sayfa`}
        description={`${BRAND_NAME} — Her anınıza uygun kaliteli giyim. Türkiye'nin en sevilen hazır giyim markası. Kadın, Erkek, Çocuk ve Bebek koleksiyonları.`}
        keywords={`hazır giyim, moda, kadın, erkek, çocuk, bebek, ${BRAND_NAME}, Türkiye`}
      />

      {/* ─── HERO ─── */}
      {heroVideo ? (
        <HeroVideo config={heroVideo} />
      ) : heroVideoChecked ? (
        <HeroCarousel />
      ) : (
        <HeroVideoPlaceholder />
      )}

      {/* ─── HAFTANIN SECTIKLERİ ─── */}
      <SectionDivider
        title="Haftanın Seçtikleri"
        subtitle="Editör seçimi, bu haftanın en çok tercih edilen parçaları"
        compact
      />

      <WeeklyFeatured products={featuredItems} loading={loading} />

      {/* ─── SON İNDİRİMLER ─── */}
      <SectionDivider
        title="Son İndirim Fırsatları"
        subtitle="Sezon sonu indirimleri kaçırmayın"
        ctaText="Tümünü Gör"
        ctaLink="/sale"
        accent="red"
        compact
      />

      <SaleGrid products={saleItems} loading={loading} />

      <SectionDivider />

      <CategoryCarousel />

      {/* ─── YENİ SEZON ─── */}
      <SectionDivider
        title="Yeni Sezon"
        subtitle={
          <>
            Sezonun en yeni parçaları şimdi <BrandWordmark size="inline" />'da
          </>
        }
        ctaText="Keşfet"
        ctaLink="/new-arrivals"
        compact
      />

      {loading ? (
        <div className="py-5 sm:py-7">
          <h2 className="text-2xl font-bold mb-4 text-center">{t('home.newArrivals')}</h2>
          <ProductGridSkeleton columns={4} count={4} />
        </div>
      ) : (
        <ProductCarousel
          title={t('home.newArrivals')}
          products={newArrivals}
          viewAllLink="/new-arrivals"
          autoScroll
          compactSection
        />
      )}

      <SectionDivider />

      {loading ? (
        <div className="py-5 sm:py-7">
          <h2 className="text-2xl font-bold mb-4 text-center">{t('home.bestSellers')}</h2>
          <ProductGridSkeleton columns={4} count={4} />
        </div>
      ) : (
        <ProductCarousel
          title={t('home.bestSellers')}
          products={bestSellers}
          viewAllLink="/bestsellers"
          autoScroll
          compactSection
        />
      )}

      {/* ─── ÖZEL KOLEKSİYON ─── */}
      <SectionDivider
        title="Özel Koleksiyon"
        subtitle="Size özel tasarlanmış, limitli sayıda üretilen parçalar"
        compact
      />

      <PromoBanner />

      <SectionDivider />

      <FeaturedCollections />

      {/* ─── TREND OLAN ─── */}
      <SectionDivider
        title="Trend Olan"
        subtitle="Şu an en çok aranan ve satılan ürünler"
        ctaText="Hepsini Gör"
        ctaLink="/sale"
        accent="red"
        compact
      />

      {loading ? (
        <div className="py-5 sm:py-7">
          <h2 className="text-2xl font-bold mb-4 text-center">{t('home.trendingNow')}</h2>
          <ProductGridSkeleton columns={4} count={4} />
        </div>
      ) : (
        <ProductCarousel
          title={t('home.trendingNow')}
          products={saleItems}
          viewAllLink="/sale"
          autoScroll
          compactSection
        />
      )}
    </Layout>
  );
};

export default HomePage;
